import React, { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "./UI";

const navItems = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about_me" },
  { label: "Recognition", path: "/recognition" },
  { label: "Blog", path: "/blog" },
  { label: "Connect", path: "/connect_me" },
];

const Navbar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the mobile menu whenever the route changes.
  useEffect(() => setOpen(false), [pathname]);

  const solid = scrolled || pathname !== "/";

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-colors duration-300",
        solid
          ? "border-b border-white/10 bg-ink-950/80 backdrop-blur-md"
          : "border-b border-transparent bg-transparent"
      )}
    >
      <nav className="container mx-auto flex h-16 items-center justify-between md:h-20">
        <Link to="/" className="flex items-center gap-2.5" aria-label="Home">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-brand-500 font-display text-base font-bold text-ink-950">
            DA
          </span>
          <span className="font-display text-base font-semibold tracking-tight text-white">
            Devyansh&nbsp;Agrawal
          </span>
        </Link>

        <ul className="hidden items-center gap-8 md:flex">
          {navItems.map((item) => {
            const active = item.path === pathname;
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={cn(
                    "text-sm font-medium transition-colors hover:text-brand-400",
                    active ? "text-brand-400" : "text-zinc-300"
                  )}
                >
                  {item.label}
                </Link>
              </li>
            );
          })}
          <li>
            <a
              href="https://devananta.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white transition-colors hover:border-brand-500/60 hover:text-brand-400"
            >
              DevAnanta <ArrowUpRight className="h-4 w-4" />
            </a>
          </li>
        </ul>

        <button
          className="text-white md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {/* Mobile drawer */}
      <div
        className={cn(
          "overflow-hidden border-t border-white/10 bg-ink-950/95 backdrop-blur-md transition-[max-height] duration-300 md:hidden",
          open ? "max-h-96" : "max-h-0"
        )}
      >
        <ul className="container mx-auto flex flex-col gap-1 py-4">
          {navItems.map((item) => (
            <li key={item.path}>
              <Link
                to={item.path}
                className="block rounded-lg px-3 py-3 text-base font-medium text-zinc-200 hover:bg-white/5 hover:text-brand-400"
              >
                {item.label}
              </Link>
            </li>
          ))}
          <li>
            <a
              href="https://devananta.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 rounded-lg px-3 py-3 text-base font-semibold text-brand-400"
            >
              Visit DevAnanta <ArrowUpRight className="h-4 w-4" />
            </a>
          </li>
        </ul>
      </div>
    </header>
  );
};

export default Navbar;
